import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useDataRefresh, DEFAULT_POLL_INTERVAL_MS } from '@/hooks/useDataRefresh';
import { Plus, Search, Wrench, Edit, AlertTriangle } from 'lucide-react';
import { ToolFormDialog } from '@/components/Tools/ToolFormDialog';
import type { Tool } from '@/types/legacy';

export default function Tools() {
  const { userRole } = useAuth();
  const { toast } = useToast();
  const [tools, setTools] = useState<Tool[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTool, setSelectedTool] = useState<Tool | null>(null);

  const fetchTools = async () => {
    try {
      const { data, error } = await api
        .from('tools')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTools(data || []);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las herramientas',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTools();
  }, []);
  useDataRefresh(fetchTools, { pollIntervalMs: DEFAULT_POLL_INTERVAL_MS });

  const filteredTools = tools.filter(tool =>
    tool.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tool.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tool.type?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = async (tool: any) => {
    if (!window.confirm(`¿Eliminar la herramienta "${tool.name}"? Se marcará como inactivo y se conserva para trazabilidad.`)) return;
    const { error } = await api.from('tools').update({ is_active: false, updated_at: new Date().toISOString() }).eq('id', tool.id);
    if (error) { toast({ title: 'Error', description: 'No se pudo eliminar: ' + error.message, variant: 'destructive' }); return; }
    toast({ title: 'Eliminado', description: 'Marcado como inactivo (histórico conservado).' });
    fetchTools();
  };

  const handleEdit = (tool: Tool) => {
    setSelectedTool(tool);
    setDialogOpen(true);
  };

  const handleNew = () => {
    setSelectedTool(null);
    setDialogOpen(true);
  };

  const canManage = userRole === 'admin' || userRole === 'supervisor';

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
      available: { label: 'Disponible', variant: 'default' },
      in_use: { label: 'En Uso', variant: 'secondary' },
      maintenance: { label: 'Mantenimiento', variant: 'outline' },
      damaged: { label: 'Dañada', variant: 'destructive' },
    };
    const config = statusConfig[status] || statusConfig.available;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
              Gestión de Herramientas
            </h1>
            <p className="text-muted-foreground mt-2">
              Administración de herramientas de producción
            </p>
          </div>
          {canManage && (
            <Button onClick={handleNew} className="bg-gradient-magma hover:shadow-magma-hover">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Herramienta
            </Button>
          )}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Buscar herramientas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Herramientas</p>
                <p className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
                  {tools.length}
                </p>
              </div>
              <Wrench className="w-8 h-8 text-primary/50" />
            </div>
          </Card>

          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Disponibles</p>
                <p className="text-3xl font-bold text-success">
                  {tools.filter(t => t.status === 'available').length}
                </p>
              </div>
            </div>
          </Card>

          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">En Mantenimiento</p>
                <p className="text-3xl font-bold text-warning">
                  {tools.filter(t => t.status === 'maintenance').length}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Tools Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : filteredTools.length === 0 ? (
          <Card className="glass-card p-12 text-center">
            <Wrench className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg text-muted-foreground">
              {searchTerm ? 'No se encontraron herramientas' : 'No hay herramientas registradas'}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTools.map((tool) => (
              <Card key={tool.id} className="glass-card p-6 hover:shadow-magma transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Wrench className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{tool.name}</h3>
                      {tool.code && (
                        <p className="text-sm text-muted-foreground">{tool.code}</p>
                      )}
                    </div>
                  </div>
                  {!tool.is_active && (
                    <Badge variant="secondary" className="text-xs">
                      Inactiva
                    </Badge>
                  )}
                </div>

                {tool.type && (
                  <div className="mb-3">
                    <p className="text-sm text-muted-foreground">Tipo: {tool.type}</p>
                  </div>
                )}

                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Estado:</span>
                    {getStatusBadge(tool.status)}
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Cantidad:</span>
                    <span className="text-sm font-medium">{tool.quantity}</span>
                  </div>

                  {tool.quantity <= tool.min_quantity && (
                    <div className="flex items-center gap-2 text-warning">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="text-xs">Stock bajo</span>
                    </div>
                  )}
                </div>

                {tool.location && (
                  <div className="mb-3">
                    <p className="text-xs text-muted-foreground">
                      📍 {tool.location}
                    </p>
                  </div>
                )}

                {canManage && (
                  <div className="mt-4 pt-4 border-t border-border/50">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(tool)}
                      className="w-full"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Editar
                    </Button>
                    {tool.is_active && <Button variant="outline" size="sm" className="w-full mt-2 text-destructive" onClick={() => handleDelete(tool)}>Eliminar</Button>}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      <ToolFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        tool={selectedTool}
        onSuccess={fetchTools}
      />
    </DashboardLayout>
  );
}
